// modules
import { useCallback, useMemo, useState } from "react";

// validations
import { hasMinTenChars, hasAtleastOneUpperCase, hasAtleastOneLowerCase, hasAtleastOneSpecialChar } from "containers/FirstTimeLogin/passwordUtils";

type WatchWordChecks = {
	isMinChars: boolean;
	isUpperCase: boolean;
	isLowerCase: boolean;
	isSpecialChar: boolean;
};

const initialStateForError = {
	isMinChars: false,
	isUpperCase: false,
	isLowerCase: false,
	isSpecialChar: false
};

export type FormDataValues = { password: string; confirm_password: string; verification_code: string };
export type WatchWordValidationState = "valid" | "invalid" | "";

export function useFirstTimeLogin() {
	const [watchWord, setWatchWord] = useState<string>("");
	const [isValidWatchWord, setIsWatchWordValid] = useState<WatchWordValidationState>("");
	const [confirmWatchword, setConfirmWatchword] = useState<string>("");
	const [watchWordChecks, setWatchWordChecks] = useState<WatchWordChecks>(initialStateForError);

	const validateWatchword = useCallback(
		(value: string) => {
			const checks = {
				isMinChars: false,
				isUpperCase: false,
				isLowerCase: false,
				isSpecialChar: false
			};

			if (hasMinTenChars(value)) {
				checks.isMinChars = true;
			}
			if (hasAtleastOneUpperCase(value)) {
				checks.isUpperCase = true;
			}
			if (hasAtleastOneLowerCase(value)) {
				checks.isLowerCase = true;
			}
			if (hasAtleastOneSpecialChar(value)) {
				checks.isSpecialChar = true;
			}

			const isPasswordInvalid = !!Object.values(checks)?.filter((_value) => !_value)?.length;

			return { checks, isPasswordInvalid };
		},
		[setWatchWordChecks, setWatchWord, watchWordChecks, watchWord]
	);

	const onWatchwordChange = useCallback(
		(value: string) => {
			const { checks } = validateWatchword(value);

			setWatchWord(value);
			setWatchWordChecks({ ...watchWordChecks, ...checks });
			setIsWatchWordValid("");
		},
		[setWatchWordChecks, setWatchWord, watchWordChecks, watchWord]
	);

	const onWatchwordBlur = useCallback(() => {
		if (watchWord) {
			const { isPasswordInvalid } = validateWatchword(watchWord);

			if (!isPasswordInvalid) {
				setIsWatchWordValid("valid");
			} else {
				setIsWatchWordValid("invalid");
			}
		}
	}, [validateWatchword, setIsWatchWordValid, isValidWatchWord, setWatchWordChecks, setWatchWord, watchWordChecks, watchWord]);

	const isDetailsValid = useMemo(() => {
		const { isPasswordInvalid } = validateWatchword(watchWord);
		if (watchWord === confirmWatchword && !isPasswordInvalid) {
			return true;
		}
		return false;
	}, [watchWord, confirmWatchword, validateWatchword]);

	const resetAllState = useCallback(() => {
		setWatchWord("");
		setIsWatchWordValid("");
		setConfirmWatchword("");
		setWatchWordChecks(initialStateForError);
	}, []);

	return {
		watchWord,
		watchWordChecks,
		confirmWatchword,
		isValidWatchWord,
		isDetailsValid,
		resetAll: resetAllState,
		validateWatchword,
		onWatchwordBlur,
		onWatchwordChange,
		setConfirmWatchword
	};
}
